import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Plot from "react-plotly.js";
import { Config, Layout, PlotMouseEvent } from "plotly.js";
import { ProgressSpinner } from "primereact/progressspinner";
import {
    mapGroupedPscRiskIndicators,
    mapPscRiskIndicators,
    pscRiskCategoryOrder,
    PscRiskIndicator
} from "../../utils/PscRiskGraphHelper";

interface PscRiskGraphProps {
    risks: PscRiskIndicator[];
    loading: boolean;
    error?: boolean;
    onClick?: (event: Readonly<PlotMouseEvent>) => void;
    className?: string;
    /**
     * The dashboard says nothing about filters, because unlike the Defects tab
     * it counts the whole fleet.
     */
    emptyMessage?: string;
    /**
     * The legend title. Ignored unless the risks carry groups, since the
     * ungrouped chart is one trace and shows no legend.
     */
    legendText?: string;
}

export const PscRiskGraph = ({
    risks,
    loading,
    error,
    onClick,
    className = "defect-insight-graph",
    emptyMessage,
    legendText
}: PscRiskGraphProps) => {
    const { t } = useTranslation("locale");

    // Driven by the data rather than by legendText, so the dashboard tile and
    // an unfiltered Defects tab keep the single navy trace.
    const grouped = useMemo(
        () => risks.some((risk) => risk.groups.length > 0),
        [risks]
    );

    const data = useMemo(
        () =>
            grouped
                ? mapGroupedPscRiskIndicators(risks)
                : [mapPscRiskIndicators(risks)],
        [risks, grouped]
    );

    const layout = useMemo(() => {
        const graphLayout: Partial<Layout> = {
            autosize: true,
            xaxis: {
                showgrid: true,
                gridcolor: "#B1E1E4",
                zerolinecolor: "#B1E1E4",
                fixedrange: true,
                tickfont: {
                    color: "#555",
                    size: 13
                }
            },
            yaxis: {
                showgrid: false,
                automargin: true,
                // Highest count at the top.
                autorange: "reversed",
                // Stacked, each trace holds a subset of the bars, so trace
                // order no longer orders the axis.
                categoryarray: grouped
                    ? pscRiskCategoryOrder(risks)
                    : undefined,
                categoryorder: grouped ? "array" : undefined,
                fixedrange: true,
                zerolinecolor: "#B1E1E4",
                tickfont: {
                    color: "#555",
                    size: 13
                },
                ticklen: 10,
                tickcolor: "rgba(0,0,0,0)"
            },
            margin: {
                pad: 0,
                l: 60,
                t: 20,
                b: 60,
                r: 0
            },
            font: {
                family: "Open Sans",
                color: "#555",
                size: 12
            },
            barmode: grouped ? "stack" : undefined,
            showlegend: grouped && !!legendText,
            legend: {
                title: {
                    font: {
                        color: "#555",
                        size: 13
                    },
                    text: legendText
                }
            },
            plot_bgcolor: "#F4F8FB",
            hovermode: "closest",
            hoverlabel: {
                bgcolor: "#1C1940",
                bordercolor: "#1C1940",
                font: {
                    color: "#FFF",
                    size: 13
                }
            }
        };

        return graphLayout;
    }, [grouped, legendText, risks]);

    const config = useMemo(() => {
        const graphConfig: Partial<Config> = {
            displayModeBar: true,
            responsive: true,
            displaylogo: false,
            scrollZoom: false,
            showTips: false,
            modeBarButtonsToRemove: [
                "pan2d",
                "zoom2d",
                "autoScale2d",
                "lasso2d",
                "select2d"
            ]
        };

        return graphConfig;
    }, []);

    if (loading) {
        return (
            <div className="flex justify-items-center align-items-center">
                <ProgressSpinner />
            </div>
        );
    }

    // A failed fetch must not read as "this fleet has no PSC risks".
    if (error) {
        return (
            <div className="flex justify-content-center">
                <p>{t("defectsGraph.pscRiskDataFailed")}</p>
            </div>
        );
    }

    if (!risks.length) {
        return (
            <div className="flex justify-content-center">
                <p>{emptyMessage ?? t("defectsGraph.noPscRiskDataAvailable")}</p>
            </div>
        );
    }

    return (
        <Plot
            useResizeHandler={true}
            data={data}
            layout={layout}
            config={config}
            className={className}
            onClick={onClick}
        />
    );
};
